/**
 * Ползунок ставки (раздел E): от 0 до `max` целыми долларами ППС, сумма
 * через `money()`. Верхняя граница — меньшее из лимита игры (`maxBet`,
 * `maxAmount`) и сбережений жизни, так что ставка никогда не больше денег
 * и никогда не отрицательна (Review Focus 1). При нуле ползунок выключен,
 * а причина написана текстом.
 */
import { t } from '@life/i18n';
import { useState } from 'preact/hooks';
import { money } from '../../lib/format';

/** Верхняя граница ставки: целое, не больше лимита игры и сбережений. */
export function betCap(limit: number, cash: number): number {
  const top = Math.min(Number.isFinite(limit) ? limit : 0, Number.isFinite(cash) ? cash : 0);
  return top > 0 ? Math.floor(top) : 0;
}

/** Начальная ставка: десятая часть лимита, но не меньше 1 и не больше лимита. */
export function initialBet(cap: number): number {
  if (cap <= 0) return 0;
  return Math.min(cap, Math.max(1, Math.floor(cap / 10)));
}

/** Ставка в состоянии экрана, зажатая в [0, cap]. */
export function useBet(cap: number): [number, (v: number) => void] {
  const [bet, setBet] = useState(() => initialBet(cap));
  const value = Math.min(Math.max(0, bet), cap);
  return [value, (v: number) => setBet(Math.min(Math.max(0, Math.floor(v)), cap))];
}

export interface BetProps {
  cap: number;
  value: number;
  onChange: (v: number) => void;
  /** Подпись; по умолчанию «Ставка». */
  label?: string;
}

export function Bet({ cap, value, onChange, label }: BetProps) {
  const text = label ?? t('ui.minigame.bet');
  return (
    <div class="bet">
      <label>
        <span>{text}</span>
        <span class="mono">{money(value)}</span>
      </label>
      <input
        type="range"
        min={0}
        max={cap}
        step={1}
        value={value}
        disabled={cap <= 0}
        aria-label={text}
        aria-valuetext={money(value)}
        onInput={(e) => onChange(Number((e.currentTarget as HTMLInputElement).value))}
      />
      <p class="hint">{cap > 0 ? t('ui.minigame.bet.max', { sum: money(cap) }) : t('ui.minigame.bet.none')}</p>
    </div>
  );
}
